<script setup>
	import { ref, computed } from 'vue';
	import { DsButton, DsText, dsUseIsMobile } from '@coloplast/design-system';
	import ContentIconText from 'shared/components/contentLibrary/contentIconText.vue';
	import ContentGateButton from 'shared/components/contentLibrary/contentGateButton.vue';
	import initCampaignIdSFDC from 'shared/vanilla/initSendCampaignIDtoSFDC';

	const props = defineProps({
		vm: {
			type: Object,
			default: () => {},
		},
		businessCategories: {
			type: String,
			default: '',
		},
		endorsedImage: {
			type: String,
			required: false,
		},
	});

	const { isMobile } = dsUseIsMobile();

	const shouldTrackId = ref(props?.vm?.BaseViewModel?.ShouldTrackSfdcCampaign);

	const dynamicSize = computed(() => (isMobile?.value ? 'xs' : 'sm'));

	const businessAreaCategories = computed(() => {
		let categoriesArray = props.businessCategories;
		if (categoriesArray) {
			categoriesArray = categoriesArray.split(', ');
		}
		return categoriesArray;
	});

	const formattedPriceLabel = computed(() => {
		if (props.vm.CurrentPage?.EventPriceLabel?.endsWith(':')) {
			return props.vm.CurrentPage.EventPriceLabel.slice(0, -1);
		} else {
			return props.vm.CurrentPage.EventPriceLabel;
		}
	});
</script>

<template>
	<div class="c-content-info-card" :class="{'--formpresented' : vm.BaseViewModel.IsFormPresented}">
		<div class="c-content-info-card__content-wrapper">
			<div>
				<DsText
					v-if="vm.CurrentPage.EventDuration && vm.BaseViewModel?.ScheduleViewModel"
					text-style="medium"
					text-type="body-sm"
					class="c-content-info-card__duration"
				>
					{{ vm.CurrentPage.EventDuration }}
				</DsText>
				<DsText
					v-if="vm.BaseViewModel.EventStartsDateFormatted || vm.CurrentPage.CourseInfoLabel"
					html-element="h3"
					text-type="heading-3xl"
					class="c-content-info-card__heading"
				>
					{{ vm.BaseViewModel.EventStartsDateFormatted || vm.CurrentPage.CourseInfoLabel }}
				</DsText>
				<div
					v-if="vm.BaseViewModel?.ScheduleViewModel?.length > 1"
					class="c-content-info-card__schedule-block"
				>
					<DsText
						v-for="(scheduleItem, index) in vm.BaseViewModel.ScheduleViewModel"
						:key="index"
						html-element="span"
						text-type="body-sm"
					>
						<span v-text="scheduleItem.FormattedDate"></span>
						<span>, </span>
						<span v-text="scheduleItem.FormattedStartTime"></span>
						<span> - </span>
						<span v-text="scheduleItem.FormattedEndTime"></span>
						<span
							v-if="index !== vm.BaseViewModel.ScheduleViewModel.length - 1"
							style="color: #00a2ba"
						>
							&nbsp;/&nbsp;
						</span>
					</DsText>
				</div>
			</div>
			<template v-if="!vm.BaseViewModel.IsPastEvent">
				<div 
					 :data-price="vm.BaseViewModel.FormattedPrice"
					 class="c-content-info-card__button"
					 :class="{'--formpresented' : vm.BaseViewModel.IsFormPresented}"
					 >

					<DsButton v-if="vm.CurrentPage?.EnrollToEventLabel && vm.BaseViewModel.EnrollToEventFullUrl"
							  :href="vm.BaseViewModel.EnrollToEventFullUrl"
							  variant="primary"
							  @click="shouldTrackId && initCampaignIdSFDC(vm?.CurrentPage?.CampaignId, vm?.CurrentPage?.CampaignStatus)"
							  >
						{{ vm.CurrentPage.EnrollToEventLabel }}
					</DsButton>
					<!--Cta with form kit popup-->
					<slot></slot>
				</div>

			</template>
			<div
				v-if="vm.BaseViewModel.ShowpadStartCourseUrl && vm.CurrentPage.EnrollToCourseLabel"
				class="c-content-info-card__button"
			>
				<ContentGateButton
					:vm="vm"
					:label="vm.CurrentPage.EnrollToCourseLabel"
					:url="vm.BaseViewModel.ShowpadStartCourseUrl"
					:is-button-gated="vm.CurrentPage.IsHcpGatedEnrollButton"
				>
				</ContentGateButton>
			</div>
		</div>
		<ul class="c-content-info-card__item-wrapper">
			<li
				v-if="
						vm.BaseViewModel.FormattedStartTime &&
						vm.BaseViewModel.FormattedEndTime &&
						vm.BaseViewModel?.ScheduleViewModel?.length <= 1
					"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="schedule"
					:size="dynamicSize"
				>
					<span v-text="vm.BaseViewModel.FormattedStartTime"></span>
					<span> - </span>
					<span v-text="vm.BaseViewModel.FormattedEndTime"></span>
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.EventDuration && !vm.BaseViewModel?.ScheduleViewModel"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="schedule"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.EventDuration }}
				</ContentIconText>
			</li>
			<li
				v-if="
						!vm.BaseViewModel.IsPastEvent &&
						vm.CurrentPage.EventAvailabilityStatus != 0 &&
						vm.BaseViewModel.EventAvailabilityStatus
					"
				class="c-content-info-card__item"
			>
				<ds-text
					class="c-content-info-card__availability"
					:class="{ 'c-content-info-card__availability--booked': vm.CurrentPage.EventAvailabilityStatus > 90 }"
				>
					{{ vm.BaseViewModel.EventAvailabilityStatus }}
				</ds-text>
			</li>
			<li
				v-if="vm.CurrentPage.EventLocation"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="healthcare"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.EventLocation }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage?.Price > 0 && vm.BaseViewModel.FormattedPrice || vm.CurrentPage.EventPriceLabel"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="information-circle"
					:size="dynamicSize"
				>
					<span
						v-if="vm.CurrentPage?.Price > 0 && vm.BaseViewModel.FormattedPrice"
					>
						{{ vm.BaseViewModel.FormattedPrice }}&hairsp;
					</span>
					<span v-if="vm.CurrentPage.EventPriceLabel">{{ formattedPriceLabel }}</span>
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.CourseLengthLabel"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="chat-heart"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.CourseLengthLabel }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.CourseLength"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="schedule"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.CourseLength }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.EndorsedByText"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="checkmark"
					:size="dynamicSize"
				>
					<span v-if="vm.CurrentPage.EndorsedByLabel">{{ vm.CurrentPage.EndorsedByLabel }}&nbsp;</span>
					<span v-text="vm.CurrentPage.EndorsedByText"></span>
				</ContentIconText>
			</li>
			<li
				v-for="(category, index) in businessAreaCategories"
				:key="`${category}-${index}`"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="complications"
					:size="dynamicSize"
				>
					{{ category }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.ContentTypeCategoryTag"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="document"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.ContentTypeCategoryTag }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.AccreditationPoints"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="education"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.AccreditationPoints }}
				</ContentIconText>
			</li>
			<li
				v-if="vm.CurrentPage.ShowAccreditationText && vm.CurrentPage.AccreditationText"
				class="c-content-info-card__item"
			>
				<ContentIconText
					icon="education"
					:size="dynamicSize"
				>
					{{ vm.CurrentPage.AccreditationText }}
				</ContentIconText>
			</li>
		</ul>
		<img
			v-if="vm.CurrentPage.EndorsedByLogo"
			:src="endorsedImage"
			:alt="vm.CurrentPage.EndorsedByText"
			class="c-content-info-card__image"
		/>
	</div>
</template>
